/**
 * =============================================================================
 * RECOMMENDATION SERVICE
 * =============================================================================
 * 
 * PORTFOLIO SHOWCASE: Data-Driven Recommendation Engine
 * 
 * This service demonstrates how to transform structured user profile data
 * into actionable, personalized recommendations.
 * 
 * DESIGN PATTERN: Profile → Criteria → Multi-Source Search
 * 
 * 1. PROFILE ANALYSIS
 *    - Extract user demographics (birth year → formative years)
 *    - Map heritage to genre associations
 *    - Combine family influences with personal preferences
 * 
 * 2. RECOMMENDATION GENERATION
 *    - Each recommendation is a "playlist concept" with:
 *      - id: Unique identifier
 *      - title/subtitle: User-friendly display
 *      - type: Category (era, family, cultural, language, personal)
 *      - criteria: Searchable parameters for music APIs
 *      - color: Visual theming
 * 
 * 3. MULTI-SOURCE SEARCH
 *    - Spotify: Uses advanced search syntax (year:, artist:, genre:)
 *    - YouTube: Uses natural language queries
 *    - Local Library: Uses metadata matching
 * 
 * REUSABILITY:
 * This pattern adapts to any recommendation domain:
 * - Movies: Profile → (genres, directors, eras, languages) → Netflix/Prime/YouTube
 * - Books: Profile → (genres, authors, themes) → Kindle/Audible/Library APIs
 * - Products: Profile → (categories, brands, price range) → Shopping APIs
 * 
 * CULTURAL INTELLIGENCE:
 * The mapHeritageToGenres() method is a unique feature that connects
 * cultural identity to musical genres - enabling recommendations that
 * respect and celebrate user heritage.
 * 
 * =============================================================================
 */

import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

class RecommendationService {
  constructor() {
    this.profile = null;
    this.recommendations = [];
  }

  /**
   * Load user's music profile from Firebase
   */
  async loadProfile(userId) {
    try {
      const profileRef = doc(db, 'musicProfiles', userId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        this.profile = profileSnap.data();
        console.log('📋 Profile loaded for recommendations');
        return this.profile;
      }
      return null;
    } catch (err) {
      console.error('Error loading profile:', err);
      return null;
    }
  }

  /**
   * Calculate user's formative years (ages 10-20)
   * These are the years when musical taste is most influenced
   */
  getFormativeYears() {
    if (!this.profile?.dateOfBirth) return null;
    
    const birthYear = new Date(this.profile.dateOfBirth).getFullYear();
    return {
      start: birthYear + 10,
      end: birthYear + 20,
      decades: this.getDecadesInRange(birthYear + 10, birthYear + 20)
    };
  }

  /**
   * Get decades that fall within a year range
   */
  getDecadesInRange(startYear, endYear) {
    const decades = [];
    const startDecade = Math.floor(startYear / 10) * 10;
    const endDecade = Math.floor(endYear / 10) * 10;
    
    for (let decade = startDecade; decade <= endDecade; decade += 10) {
      decades.push(`${decade}s`);
    }
    return decades;
  }

  /**
   * Generate all recommendations based on profile
   */
  async generateRecommendations(userId) {
    if (!this.profile) {
      await this.loadProfile(userId);
    }
    
    if (!this.profile) {
      return { error: 'No profile found. Please complete your Music Profile first.' };
    }

    this.recommendations = [];

    // 1. Formative Years playlist
    const formativeYears = this.getFormativeYears();
    if (formativeYears) {
      this.recommendations.push({
        id: 'formative-years',
        title: `Your ${formativeYears.decades[0]} Flashback`,
        subtitle: `Hits from when you were growing up (${formativeYears.start}-${formativeYears.end})`,
        icon: '🎸',
        type: 'era',
        criteria: {
          decades: formativeYears.decades,
          years: { start: formativeYears.start, end: formativeYears.end }
        },
        color: '#FF6B6B'
      });
    }

    // 2. Mother's Favorites
    if (this.profile.motherGenres?.length > 0 || this.profile.motherArtistsList?.length > 0) {
      this.recommendations.push({
        id: 'moms-favorites',
        title: "Mom's Favorites",
        subtitle: `Music your mother loved: ${this.profile.motherGenres?.slice(0, 3).join(', ') || 'Various'}`,
        icon: '👩',
        type: 'family',
        criteria: {
          genres: this.profile.motherGenres || [],
          artists: this.profile.motherArtistsList || [],
          eras: this.profile.motherMusicEras || [],
          customArtists: this.profile.motherArtists || ''
        },
        color: '#FF85A2'
      });
    }

    // 3. Father's Favorites
    if (this.profile.fatherGenres?.length > 0 || this.profile.fatherArtistsList?.length > 0) {
      this.recommendations.push({
        id: 'dads-favorites',
        title: "Dad's Favorites",
        subtitle: `Music your father loved: ${this.profile.fatherGenres?.slice(0, 3).join(', ') || 'Various'}`,
        icon: '👨',
        type: 'family',
        criteria: {
          genres: this.profile.fatherGenres || [],
          artists: this.profile.fatherArtistsList || [],
          eras: this.profile.fatherMusicEras || [],
          customArtists: this.profile.fatherArtists || ''
        },
        color: '#4ECDC4'
      });
    }

    // 4. Heritage Mix
    if (this.profile.heritage?.length > 0) {
      const heritageGenres = this.mapHeritageToGenres(this.profile.heritage);
      this.recommendations.push({
        id: 'heritage-mix',
        title: 'Your Heritage Mix',
        subtitle: `Music from your roots: ${this.profile.heritage.slice(0, 2).join(', ')}`,
        icon: '🌍',
        type: 'cultural',
        criteria: {
          heritage: this.profile.heritage,
          suggestedGenres: heritageGenres
        },
        color: '#9B59B6'
      });
    }

    // 5. Language-based playlist
    if (this.profile.languages?.length > 0) {
      const hasSpanish = this.profile.languages.includes('Spanish');
      const hasEnglish = this.profile.languages.includes('English');
      
      if (hasSpanish && hasEnglish) {
        this.recommendations.push({
          id: 'bilingual-mix',
          title: 'Bilingual Beats',
          subtitle: 'The best of both worlds - English & Spanish hits',
          icon: '🗣️',
          type: 'language',
          criteria: {
            languages: ['English', 'Spanish']
          },
          color: '#3498DB'
        });
      } else if (hasSpanish) {
        this.recommendations.push({
          id: 'spanish-hits',
          title: 'Spanish Hits',
          subtitle: 'Lo mejor de la música en español',
          icon: '🇪🇸',
          type: 'language',
          criteria: {
            languages: ['Spanish']
          },
          color: '#E74C3C'
        });
      }
    }

    // 6. Your Favorites (based on user's own preferences)
    if (this.profile.favoriteGenres?.length > 0) {
      this.recommendations.push({
        id: 'your-favorites',
        title: 'Made For You',
        subtitle: `Based on your taste: ${this.profile.favoriteGenres.slice(0, 3).join(', ')}`,
        icon: '⭐',
        type: 'personal',
        criteria: {
          genres: this.profile.favoriteGenres,
          eras: this.profile.favoriteEras || []
        },
        color: '#F39C12'
      });
    }

    // 7. Nostalgia Mix (combines family influences)
    const familyGenres = [...new Set([
      ...(this.profile.motherGenres || []),
      ...(this.profile.fatherGenres || [])
    ])];
    
    if (familyGenres.length > 0) {
      this.recommendations.push({
        id: 'nostalgia-mix',
        title: 'Nostalgia Mix',
        subtitle: 'Songs that remind you of home',
        icon: '💭',
        type: 'nostalgia',
        criteria: {
          genres: familyGenres,
          includeCarMusic: this.profile.childhoodCarMusic,
          includeHolidayMusic: this.profile.childhoodHolidayMusic
        },
        color: '#1ABC9C'
      });
    }

    // 8. Sunday Morning (based on grandparents/traditional)
    if (this.profile.grandparentsMusic) {
      this.recommendations.push({
        id: 'sunday-morning',
        title: 'Sunday Morning Classics',
        subtitle: 'Traditional sounds from generations past',
        icon: '☀️',
        type: 'traditional',
        criteria: {
          description: this.profile.grandparentsMusic,
          traditional: true
        },
        color: '#F1C40F'
      });
    }

    console.log(`🎵 Generated ${this.recommendations.length} recommendations`);
    return this.recommendations;
  }

  /**
   * Map heritage to suggested genres
   */
  mapHeritageToGenres(heritage) {
    const heritageGenreMap = {
      'Mexican': ['Regional Mexican', 'Rancheras', 'Mariachi', 'Cumbia', 'Boleros', 'Norteño'],
      'Hispanic/Latino': ['Latin', 'Salsa', 'Bachata', 'Reggaeton', 'Cumbia'],
      'Caribbean': ['Reggae', 'Salsa', 'Merengue', 'Calypso', 'Soca'],
      'African American': ['R&B/Soul', 'Hip Hop', 'Gospel', 'Blues', 'Jazz'],
      'African': ['Afrobeats', 'Highlife', 'Soukous', 'World Music'],
      'South American': ['Latin', 'Cumbia', 'Tango', 'Bossa Nova'],
      'Central American': ['Latin', 'Cumbia', 'Punta', 'Marimba'],
      'Western European': ['Classical', 'Pop', 'Rock', 'Folk'],
      'Eastern European': ['Classical', 'Folk', 'Polka'],
      'Asian': ['K-Pop', 'J-Pop', 'Bollywood', 'Classical'],
      'South Asian': ['Bollywood', 'Classical', 'Bhangra'],
      'Southeast Asian': ['Pop', 'Traditional', 'World Music'],
      'Middle Eastern': ['Arabic', 'World Music', 'Traditional'],
      'Pacific Islander': ['Reggae', 'Island', 'Traditional'],
      'Indigenous/Native American': ['Traditional', 'Folk', 'World Music']
    };

    const genres = new Set();
    heritage.forEach(h => {
      const mapped = heritageGenreMap[h];
      if (mapped) {
        mapped.forEach(g => genres.add(g));
      }
    });

    return Array.from(genres);
  }

  /**
   * Get Spotify search queries for a recommendation
   */
  getSpotifySearchQueries(recommendation) {
    const queries = [];
    
    switch (recommendation.type) {
      case 'era':
        recommendation.criteria.decades?.forEach(decade => {
          queries.push(`year:${decade.replace('s', '')}-${parseInt(decade) + 9}`);
        });
        break;
        
      case 'family':
      case 'personal':
        recommendation.criteria.artists?.forEach(artist => {
          queries.push(`artist:${artist}`);
        });
        recommendation.criteria.genres?.forEach(genre => {
          queries.push(`genre:${genre.toLowerCase()}`);
        });
        break;
        
      case 'language':
        if (recommendation.criteria.languages?.includes('Spanish')) {
          queries.push('genre:latin', 'genre:reggaeton', 'genre:salsa');
        }
        break;
        
      case 'cultural':
        recommendation.criteria.suggestedGenres?.forEach(genre => {
          queries.push(`genre:${genre.toLowerCase().replace(' ', '-')}`);
        });
        break;
        
      default:
        recommendation.criteria.genres?.forEach(genre => {
          queries.push(`genre:${genre.toLowerCase()}`);
        });
    }
    
    return queries;
  }

  /**
   * Get YouTube search queries for a recommendation
   */
  getYouTubeSearchQueries(recommendation) {
    const queries = [];
    
    switch (recommendation.type) {
      case 'era':
        recommendation.criteria.decades?.forEach(decade => {
          queries.push(`${decade} hits music`);
          queries.push(`best songs of the ${decade}`);
        });
        break;
        
      case 'family':
        recommendation.criteria.artists?.slice(0, 5).forEach(artist => {
          queries.push(`${artist} greatest hits`);
        });
        recommendation.criteria.genres?.slice(0, 3).forEach(genre => {
          queries.push(`${genre} music mix`);
        });
        break;
        
      case 'cultural':
        recommendation.criteria.heritage?.forEach(h => {
          queries.push(`${h} traditional music`);
          queries.push(`${h} music mix`);
        });
        break;
        
      default:
        recommendation.criteria.genres?.slice(0, 3).forEach(genre => {
          queries.push(`${genre} music playlist`);
        });
    }
    
    return queries;
  }
}

// Export singleton instance
export const recommendationService = new RecommendationService();
export default recommendationService;

